import { router } from "@inertiajs/react";
import { t } from "@travily/i18n";
import { destinationFavoritePath, listingFavoritePath, programFavoritePath } from "@travily/routes";
import { showToast } from "@ui/components/Toast";

/**
 * Each favorable type owns its hearts behind its own nested endpoint. The type
 * string is also the key the `favorited_ids` prop groups server state under, so
 * one discriminator drives both sides of the toggle.
 */
const FAVORITE_PATHS: Record<string, (id: string) => string> = {
  Destination: destinationFavoritePath,
  Listing: listingFavoritePath,
  Program: programFavoritePath,
};

interface SubmitFavoriteToggleOptions {
  favorableType: string;
  favorableId: string | number;
  wasFavorited: boolean;
  /** Runs on a 2xx response, after the prefetch flush and toast. */
  onToggled?: () => void;
  /** Reverts optimistic state on a non-2xx response or transport error. */
  onRollback: () => void;
}

/**
 * Owns the favorite-toggle request protocol shared by every toggling surface:
 * POST/DELETE to the type's favorite resource, 204-as-success handling, the
 * toast, and rollback on failure. The 204 never fires Inertia's `success`
 * event, so the provider's flush-on-mutation misses toggles — the prefetch
 * cache is flushed here instead, or prefetched pages would serve stale
 * favorite state.
 */
export function submitFavoriteToggle({
  favorableType,
  favorableId,
  wasFavorited,
  onToggled,
  onRollback,
}: SubmitFavoriteToggleOptions): void {
  const path = FAVORITE_PATHS[favorableType]?.(String(favorableId));
  if (!path) {
    onRollback();
    return;
  }

  // i18n-tasks-use t('web.favorites.create_success.title')
  // i18n-tasks-use t('web.favorites.create_success.description')
  // i18n-tasks-use t('web.favorites.destroy_success.title')
  // i18n-tasks-use t('web.favorites.destroy_success.description')
  const toastBase = wasFavorited ? "web.favorites.destroy_success" : "web.favorites.create_success";
  const options = {
    preserveScroll: true,
    onHttpException: (response: { status: number }) => {
      if (response.status >= 200 && response.status < 300) {
        router.flushAll();
        showToast.success(t(`${toastBase}.title`), t(`${toastBase}.description`));
        onToggled?.();
      } else {
        onRollback();
      }
      return false;
    },
    onError: onRollback,
  };

  if (wasFavorited) {
    router.delete(path, options);
  } else {
    router.post(path, {}, options);
  }
}
